import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Search, ArrowUpRight, Clock, Check } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { Wordmark } from "@/components/landing/Wordmark";
import { MiniArt } from "@/components/academy/MiniArt";
import { SCHOOLS, PATHS, GUIDES, GLOSSARY } from "@/data/academy";
import { LESSONS } from "@/data/lessons";
import { useSeo } from "@/lib/seo";

const KEY = "tq_academy_done";
export const getDone = () => { try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch { return new Set(); } };
export const markDone = (id) => { const s = getDone(); s.add(id); localStorage.setItem(KEY, JSON.stringify([...s])); };

const ART_CYCLE = ["candles", "structure", "zones", "sweep", "bars", "line", "r", "fan"];
const CHART_SCHOOL = { id: "charts", n: "13", t: "Chart patterns library", level: "Beginner", paths: ["scalper", "intraday", "investor"], d: "Candles, support & resistance and the classic patterns, with published statistics.", after: "You can name any pattern and know how often it actually works.",
  lessons: LESSONS.map((l, i) => ({ t: l.title, href: `/learn/${l.slug}`, m: l.readMins, art: ART_CYCLE[i % ART_CYCLE.length], take: l.summary, id: `lesson:${l.slug}`, internal: true })) };
const ALL = [...SCHOOLS, CHART_SCHOOL];
const TABS = [["course", "Course"], ["guides", "Tool guides"], ["routines", "Routines"], ["glossary", "Glossary"]];

const LessonCard = ({ l, school, done, onOpen }) => {
  const inner = (
    <>
      <div className="relative h-32 border-b border-white/[0.06] bg-[#0D0E11] p-4">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#5A6170]">{school}</p>
        <p className="mt-1 max-w-[58%] font-display text-[15px] font-semibold leading-tight tracking-[-0.02em] text-[#ECEBE4]">{l.t}</p>
        <MiniArt kind={l.art} className="absolute bottom-3 right-3 h-12 w-24 opacity-90" />
        <span className="absolute bottom-3 left-4 inline-flex items-center gap-1 font-mono text-[10px] text-[#9DA3AE]"><Clock size={10} />{l.m} min</span>
        {done && <span className="absolute right-3 top-3 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[#BACD1C]"><Check size={11} />Done</span>}
      </div>
      <p className="p-4 text-[13px] leading-snug text-[#9DA3AE]">{l.take}</p>
    </>
  );
  const cls = "group block border border-white/[0.07] bg-[#0B0C0F] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/20";
  return l.internal
    ? <Link to={l.href} onClick={onOpen} data-testid={`lesson-card-${l.id}`} className={cls}>{inner}</Link>
    : <a href={l.href} onClick={onOpen} data-testid={`lesson-card-${l.id}`} className={cls}>{inner}</a>;
};

const SchoolBlock = ({ s, done, open }) => {
  const n = s.lessons.filter((l) => done.has(l.id)).length;
  const mins = s.lessons.reduce((a, l) => a + l.m, 0);
  const next = s.lessons.find((l) => !done.has(l.id)) || s.lessons[0];
  return (
    <section data-testid={`school-${s.id}`} className="mt-14">
      <div className="grid gap-6 border border-white/[0.07] bg-[#0B0C0F] p-6 md:grid-cols-[1.4fr_1fr_auto] md:items-center md:p-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">School {s.n} · <span className="text-[#9DA3AE]">{s.level}</span></p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-[-0.03em] text-[#ECEBE4] md:text-3xl">{s.t}</h2>
          <p className="mt-2 text-[15px] text-[#9DA3AE]">{s.d}</p>
        </div>
        <div className="border-l border-white/[0.08] pl-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">After this part</p>
          <p className="mt-1.5 text-[14px] text-[#ECEBE4]">{s.after}</p>
        </div>
        <div className="min-w-[200px]">
          <div className="flex gap-6 font-mono">
            <div><div className="text-xl text-[#ECEBE4]">{s.lessons.length}</div><div className="text-[10px] uppercase tracking-[0.16em] text-[#5A6170]">lessons</div></div>
            <div><div className="text-xl text-[#ECEBE4]">{mins}m</div><div className="text-[10px] uppercase tracking-[0.16em] text-[#5A6170]">total</div></div>
            <div><div data-testid={`school-progress-${s.id}`} className="text-xl text-[#ECEBE4]">{n}/{s.lessons.length}</div><div className="text-[10px] uppercase tracking-[0.16em] text-[#5A6170]">done</div></div>
          </div>
          <div className="mt-3 h-[3px] bg-white/[0.06]"><div className="h-full bg-[#BACD1C]/80 transition-[width] duration-500" style={{ width: `${(n / s.lessons.length) * 100}%` }} /></div>
          <a href={next.href} onClick={() => open(next.id)} data-testid={`school-start-${s.id}`} className="mt-4 flex items-center justify-center gap-2 border border-white/20 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#ECEBE4] transition-colors hover:border-white/50">
            {n ? "Continue" : "Start"} · {next.t.split(":")[0].slice(0, 22)}{next.t.length > 22 ? "…" : ""}
          </a>
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {s.lessons.map((l) => <LessonCard key={l.id} l={l} school={s.t} done={done.has(l.id)} onOpen={() => open(l.id)} />)}
      </div>
    </section>
  );
};

const GuideGrid = ({ kind }) => (
  <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
    {GUIDES.filter((g) => g.kind === kind).map((g) => (
      <Link key={g.slug} to={`/learn/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group border border-white/[0.07] bg-[#0B0C0F] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/20">
        <div className="h-36 overflow-hidden border-b border-white/[0.06]"><img src={g.img} alt="" loading="lazy" width={1600} height={900} className="h-full w-full object-cover object-left-top opacity-80 transition-opacity group-hover:opacity-100" /></div>
        <div className="p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">{kind === "tool" ? "Tool guide" : "Routine"} · {g.mins} min read</p>
          <h3 className="mt-2 font-display text-xl font-semibold tracking-[-0.02em] text-[#ECEBE4]">{g.t}</h3>
          <p className="mt-1 text-[14px] text-[#9DA3AE]">{g.sub}</p>
        </div>
      </Link>
    ))}
  </div>
);

const Glossary = ({ q }) => {
  const rows = GLOSSARY.filter(([t, d]) => !q || (t + d).toLowerCase().includes(q.toLowerCase()));
  return (
    <dl data-testid="glossary-list" className="mt-10 grid gap-px border border-white/[0.07] bg-white/[0.07] md:grid-cols-2">
      {rows.map(([t, d]) => (
        <div key={t} id={`g-${t}`} data-testid="glossary-term" className="bg-[#08090B] p-5">
          <dt className="font-display text-[17px] font-semibold tracking-[-0.02em] text-[#ECEBE4]">{t}</dt>
          <dd className="mt-1.5 text-[14px] leading-relaxed text-[#9DA3AE]">{d}</dd>
        </div>
      ))}
      {!rows.length && <p className="bg-[#08090B] p-6 font-mono text-xs text-[#5A6170]">No term matches “{q}”.</p>}
    </dl>
  );
};

const SearchResults = ({ q, open }) => {
  const s = q.toLowerCase();
  const lessons = ALL.flatMap((sc) => sc.lessons.map((l) => ({ ...l, school: sc.t }))).filter((l) => (l.t + l.take).toLowerCase().includes(s)).slice(0, 8);
  const guides = GUIDES.filter((g) => (g.t + g.sub + g.what).toLowerCase().includes(s));
  const terms = GLOSSARY.filter(([t, d]) => (t + d).toLowerCase().includes(s)).slice(0, 6);
  return (
    <div data-testid="academy-search-results" className="mt-10 space-y-10">
      {!!lessons.length && <div><p className="mb-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">Lessons</p><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{lessons.map((l) => <LessonCard key={l.id} l={l} school={l.school} done={false} onOpen={() => open(l.id)} />)}</div></div>}
      {!!guides.length && <div><p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">Guides & routines</p>{guides.map((g) => <Link key={g.slug} to={`/learn/guide/${g.slug}`} className="flex justify-between border-b border-white/[0.06] py-3 text-[15px] text-[#ECEBE4] hover:text-[#BACD1C]">{g.t}<ArrowUpRight size={14} /></Link>)}</div>}
      {!!terms.length && <div><p className="mb-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">Glossary</p>{terms.map(([t, d]) => <p key={t} className="border-b border-white/[0.06] py-3 text-[14px] text-[#9DA3AE]"><span className="text-[#ECEBE4]">{t}</span> · {d}</p>)}</div>}
      {!lessons.length && !guides.length && !terms.length && <p className="font-mono text-xs text-[#5A6170]">Nothing found for “{q}”.</p>}
    </div>
  );
};

export default function LearnHub() {
  useSeo({ title: "TradrQuant Academy | Free crypto trading course, tool guides & glossary", description: "Free crypto trading academy: 13 schools from market plumbing to order flow, derivatives and risk, plus tool guides, routines and a trading glossary.", path: "/learn" });
  const [tab, setTab] = useState("course");
  const [path, setPath] = useState("all");
  const [q, setQ] = useState("");
  const [done, setDone] = useState(getDone);
  const input = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const k = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); input.current?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const open = (id) => { markDone(id); setDone(getDone()); };
  const schools = useMemo(() => ALL.filter((s) => path === "all" || s.paths.includes(path)), [path]);
  const total = ALL.reduce((a, s) => a + s.lessons.length, 0);
  const doneN = ALL.reduce((a, s) => a + s.lessons.filter((l) => done.has(l.id)).length, 0);
  const next = ALL.flatMap((s) => s.lessons).find((l) => !done.has(l.id));

  return (
    <div data-testid="learn-hub" className="min-h-screen bg-[#08090B] text-[#ECEBE4]">
      <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#08090B]/95">
        <div className="mx-auto flex h-14 max-w-[1300px] items-center justify-between gap-4 px-5 md:px-10">
          <Link to="/"><Wordmark /></Link>
          <nav className="hidden gap-6 md:flex">
            {TABS.map(([id, t]) => <button key={id} data-testid={`academy-tab-${id}`} onClick={() => { setTab(id); setQ(""); }} className={`relative py-4 text-[13px] font-medium transition-colors ${tab === id && !q ? "text-[#ECEBE4]" : "text-[#7D8390] hover:text-[#ECEBE4]"}`}>{t}{tab === id && !q && <span className="absolute inset-x-0 bottom-0 h-px bg-[#ECEBE4]" />}</button>)}
          </nav>
          <label className="flex items-center gap-2 border border-white/[0.10] bg-[#0B0C0F] px-3 py-1.5">
            <Search size={13} className="text-[#5A6170]" />
            <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} data-testid="academy-search" placeholder="Search the academy" className="w-28 bg-transparent text-[13px] text-[#ECEBE4] placeholder:text-[#5A6170] focus:outline-none sm:w-44" />
            <kbd className="hidden border border-white/15 px-1.5 font-mono text-[10px] text-[#5A6170] sm:inline">/</kbd>
          </label>
        </div>
        <div className="flex gap-1 overflow-x-auto border-t border-white/[0.05] px-5 md:hidden">
          {TABS.map(([id, t]) => <button key={id} data-testid={`academy-tab-m-${id}`} onClick={() => { setTab(id); setQ(""); }} className={`shrink-0 px-3 py-3 text-[13px] ${tab === id ? "text-[#ECEBE4]" : "text-[#7D8390]"}`}>{t}</button>)}
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-5 pb-24 pt-14 md:px-10 md:pt-20">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#5A6170] hover:text-[#ECEBE4]"><ChevronLeft size={13} />Home</Link>
        <div className="mt-6 grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-end">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#7D8390]">TQ Academy · free · education only</p>
            <h1 className="mt-4 font-display text-[2.4rem] font-semibold leading-[1] tracking-[-0.04em] sm:text-6xl">Learn the market the way professionals do.</h1>
            <p className="mt-5 max-w-2xl text-lg text-[#9DA3AE]">From how your order gets filled to order flow, derivatives, quant maths and risk. Interactive lessons with simulators and live data, plus guides for every TradrQuant tool.</p>
          </div>
          <div data-testid="academy-progress" className="border border-white/[0.07] bg-[#0B0C0F] p-6">
            <div className="flex items-baseline justify-between"><span className="font-display text-4xl font-semibold">{doneN}<span className="text-[#5A6170]">/{total}</span></span><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">lessons opened</span></div>
            <div className="mt-3 h-[3px] bg-white/[0.06]"><div className="h-full bg-[#BACD1C]/80" style={{ width: `${(doneN / total) * 100}%` }} /></div>
            {next && <a href={next.href} onClick={() => open(next.id)} data-testid="academy-up-next" className="mt-5 flex items-center justify-between border-t border-white/[0.06] pt-4 text-[14px] text-[#ECEBE4] hover:text-[#BACD1C]"><span><span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">Up next</span>{next.t}</span><ArrowUpRight size={15} /></a>}
          </div>
        </div>

        {q ? <SearchResults q={q} open={open} /> : (
          <>
            {tab === "course" && (
              <>
                <div className="mt-14 flex flex-wrap items-center gap-2" data-testid="academy-paths">
                  <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#5A6170]">Choose your path</span>
                  {[{ id: "all", t: "All schools", d: `${ALL.length}` }, ...PATHS].map((p) => (
                    <button key={p.id} data-testid={`path-${p.id}`} onClick={() => setPath(p.id)} className={`border px-3.5 py-2 text-[13px] transition-colors ${path === p.id ? "border-white/40 bg-white/[0.04] text-[#ECEBE4]" : "border-white/[0.08] text-[#7D8390] hover:text-[#ECEBE4]"}`}>
                      {p.t} <span className="ml-1 font-mono text-[10px] text-[#5A6170]">{p.d}</span>
                    </button>
                  ))}
                </div>
                {schools.map((s) => <SchoolBlock key={s.id} s={s} done={done} open={open} />)}
              </>
            )}
            {tab === "guides" && <><h2 className="mt-14 font-display text-3xl font-semibold tracking-[-0.03em]">Every tool, explained with a real example.</h2><GuideGrid kind="tool" /></>}
            {tab === "routines" && <><h2 className="mt-14 font-display text-3xl font-semibold tracking-[-0.03em]">The routines that keep you consistent.</h2><GuideGrid kind="routine" /></>}
            {tab === "glossary" && <><h2 className="mt-14 font-display text-3xl font-semibold tracking-[-0.03em]">Trading glossary, in plain English.</h2><Glossary q="" /></>}
          </>
        )}
        <p className="mt-20 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A6170]">Education only · this isn't financial advice · interactive lessons open on alpha.tradrquant.com</p>
      </main>
      <Footer />
    </div>
  );
}
